//
// Copyright 2024 DXOS.org
//

import { RegistryContext } from '@effect/atom-react/RegistryContext';
import { useContext, useEffect, useRef, useState } from 'react';

import { type Database, Obj } from '@dxos/echo';
import { useManagerOptional, useSelection, useSelectionActions } from '@dxos/react-ui-attention';
import { type ProjectionModel } from '@dxos/schema';

import {
  TableModel,
  type TableModelProps,
  type TableRow,
  type TableRowAction,
  createEchoChangeCallback,
} from '../model/index.ts';
import { type Table } from '../types/index.ts';

export type UseTableModelProps<T extends TableRow = TableRow> = {
  object?: Table.Table;
  projection?: ProjectionModel;
  db?: Database.Database;
  rows?: T[];
  rowActions?: TableRowAction[];
  /**
   * Rows to select when the model is built. Overrides the selection read from the view state for
   * the table object — pass this when the surrounding container owns the selection under a
   * different context id (e.g. a type article keyed by the type's URI), so switching into the
   * table preserves what was already selected.
   */
  selection?: readonly string[];
  onSelectionChanged?: (selection: string[]) => void;
  onRowAction?: (actionId: string, data: T) => void;
} & Pick<
  TableModelProps<T>,
  'features' | 'onInsertRow' | 'onDeleteRows' | 'onColumnDelete' | 'onCellUpdate' | 'onRowOrderChange'
>;

export const useTableModel = <T extends TableRow = TableRow>({
  object,
  projection,
  db,
  rows,
  rowActions,
  features,
  selection,
  onSelectionChanged,
  onRowAction,
  ...props
}: UseTableModelProps<T>): TableModel<T> | undefined => {
  const registry = useContext(RegistryContext);
  const viewState = useManagerOptional();
  const selected = useSelection(object && Obj.getURI(object), 'multi');
  // Read at construction time, not frozen into the effect's deps. The model is rebuilt whenever any
  // of its inputs change identity, and each rebuild re-seeds from here — a frozen snapshot would
  // reset the selection to whatever it was when the table mounted, discarding the user's clicks.
  const initialSelection = useRef<readonly string[]>(selection ?? selected);
  initialSelection.current = selection ?? selected;

  const [model, setModel] = useState<TableModel<T>>();
  useEffect(() => {
    if (!object || !projection) {
      return;
    }

    let model: TableModel<T> | undefined;
    const t = setTimeout(async () => {
      model = new TableModel<T>({
        registry,
        object,
        projection,
        db,
        viewState,
        change: createEchoChangeCallback<T>(object),
        features,
        rowActions,
        initialSelection: [...initialSelection.current],
        onRowAction,
        ...props,
      });
      await model.open();
      setModel(model);
    });

    return () => {
      clearTimeout(t);
      void model?.close();
    };
    // TODO(burdon): Trigger if callbacks change?
  }, [registry, viewState, object, projection, features, rowActions]);

  // Update data when rows change.
  useEffect(() => {
    if (rows && model) {
      model.setRows(rows);
    }
  }, [model, rows]);

  const { multi, clear } = useSelectionActions(model?.id);

  useEffect(() => {
    if (!model) {
      return;
    }

    const unsubscribe = registry.subscribe(model.selection.selectionAtom, () => {
      const selectedItems = [...model.selection.selection];
      multi(selectedItems);
      onSelectionChanged?.(selectedItems);
    });

    // Maybe clear the selection here?
    return () => {
      clear();
      unsubscribe();
    };
  }, [registry, model, onSelectionChanged]);

  return model;
};
